import { FC, useEffect, useState } from 'react';
import InfoWarning from '../info-text/InfoWarning';
import NumberInput from '../custom-input/NumberInput';
import Tooltip from '../tooltip/Tooltip';
import {
  EmbryoStraw,
  SpecimenInventoryWithUseRequested,
  SpecimenLocation,
  SpecimenLocationWithStraw,
} from '../../types/interfaces/specimen.interfaces';
import { CheckOutSpecimen } from '../../types/interfaces/transaction.interfaces';
import { toastMessages } from '../../constants/errorMessages';
import { showToast } from '../../services/toast.service';
import { getEmbryoStraws } from '../../api/embryoStrawsApi';
import { LABEL_CONSTANTS, TABLE_HEADER_CONSTANTS } from '../../constants/common';

type PendingUseSelectQuantityTableProps = {
  pendingUseTransaction: SpecimenInventoryWithUseRequested;
  setCheckOutSpecimens: React.Dispatch<React.SetStateAction<CheckOutSpecimen[]>>;
  checkOutSpecimens: CheckOutSpecimen[];
  setCheckOutRowInvalid: React.Dispatch<React.SetStateAction<boolean>>;
};

const PendingUseSelectQuantityTable: FC<PendingUseSelectQuantityTableProps> = ({
  pendingUseTransaction,
  setCheckOutSpecimens,
  checkOutSpecimens,
  setCheckOutRowInvalid,
}) => {
  const [isTravelingTank, setIsTravelingTank] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [embryoStraws, setEmbryoStraws] = useState<EmbryoStraw[]>([]);

  useEffect(() => {
    setIsTravelingTank(
      pendingUseTransaction.specimenLocations?.some(
        (loc: SpecimenLocation) => loc.storageCanister?.storageTank?.isTravelingTank,
      ) ?? false,
    );
  }, [pendingUseTransaction.specimenLocations]);

  useEffect(() => {
    const fetchEmbryoStrawsByTransaction = async () => {
      if (!pendingUseTransaction?.inventoryTransactionId || !pendingUseTransaction?.specimenId) return;

      setIsLoading(true);
      try {
        const params = {
          filter: `inventoryTransactionId eq ${pendingUseTransaction.inventoryTransactionId};`,
        };
        const response = await getEmbryoStraws(params);
        const fetchedEmbryoStraws: EmbryoStraw[] = response?.data ?? [];

        const updatedSpecimenLocations =
          pendingUseTransaction.specimenLocations?.map(location => {
            const canisterId = location.storageCanisterId;
            const matchingStraws = fetchedEmbryoStraws.filter(
              straw =>
                straw.storageCanisterId !== null &&
                straw.storageCanisterId === canisterId &&
                straw.specimenId === pendingUseTransaction.specimenId,
            );

            return {
              ...location,
              embryoStraws: matchingStraws,
            };
          }) ?? [];

        setEmbryoStraws(fetchedEmbryoStraws);

        pendingUseTransaction.specimenLocations = updatedSpecimenLocations;
      } catch (error) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEmbryoStrawsByTransaction();
  }, [pendingUseTransaction?.inventoryTransactionId]);

  const isFemale = pendingUseTransaction?.animal?.gender?.toLowerCase() === 'female';

  const checkIfTransactionIsValid = (checkOutQty: number, canisterQty: number) => {
    if (checkOutQty > canisterQty) {
      showToast.error(toastMessages.CHOSE_MORE_THAN_TOTAL);
      setCheckOutRowInvalid(true);
    } else if (checkOutQty < 0 || totalSelectedQuantity < 0) {
      showToast.error(toastMessages.CHOSE_LESS_THAN_ZERO);
      setCheckOutRowInvalid(true);
    } else {
      setCheckOutRowInvalid(false);
    }
  };

  function removeTravelingTanks(inventory: SpecimenInventoryWithUseRequested[]): SpecimenInventoryWithUseRequested[] {
    inventory.forEach((i: SpecimenInventoryWithUseRequested) => {
      i.specimenLocations = i?.specimenLocations?.filter(
        (loc: SpecimenLocation) => !loc.storageCanister?.storageTank?.isTravelingTank,
      );
    });

    return inventory.filter((i: SpecimenInventoryWithUseRequested) => i.specimenLocations?.length);
  }

  const handleCheckoutQuantityChange = (e: React.ChangeEvent<HTMLInputElement>, specimenLocation: SpecimenLocation) => {
    let newQuantity = parseInt(e.target.value);
    const newCheckOutSpecimens = [...checkOutSpecimens];
    const index = newCheckOutSpecimens.findIndex(
      checkOutSpecimen => checkOutSpecimen.selectedCanisterId === specimenLocation.storageCanisterId,
    );

    if (isNaN(newQuantity)) {
      newQuantity = 0;
    }

    checkIfTransactionIsValid(newQuantity, specimenLocation?.quantity!);

    if (index != -1) {
      newCheckOutSpecimens[index].quantity = newQuantity;
      setCheckOutSpecimens(newCheckOutSpecimens);
    }
  };

  function handleSummaryClass() {
    if (totalSelectedQuantity > pendingUseTransaction.quantityOwned) {
      showToast.error(toastMessages.CHOSE_MORE_THAN_OWNED);
      setCheckOutRowInvalid(true);
      return 'invalid-row';
    } else if (totalSelectedQuantity > pendingUseTransaction.quantityAvailable) {
      return 'override-row';
    }
  }

  function handleRowClass(index: number, location: SpecimenLocation, mobileView: boolean, disabled: boolean): string {
    if (index < checkOutSpecimens.length && checkOutSpecimens[index].quantity !== undefined) {
      return `${mobileView ? 'card max-width' : 'specimen-row'} ${
        checkOutSpecimens[index].quantity > location.quantity && !disabled ? 'invalid-row' : ''
      }`;
    } else {
      return '';
    }
  }

  const getSelectedQuantity = (storageCanisterId: number) => {
    const newCheckOutSpecimens = [...checkOutSpecimens];
    const index = newCheckOutSpecimens.findIndex(checkOutSpecimen => checkOutSpecimen.selectedCanisterId === storageCanisterId);
    return index != -1 ? checkOutSpecimens[index]?.quantity.toString() : '';
  };

  const specimenRows: SpecimenLocationWithStraw[] = (() => {
    const specimenLocations = pendingUseTransaction.specimenLocations ?? [];

    if (isFemale) {
      return specimenLocations.flatMap((location: SpecimenLocation & { embryoStraws?: EmbryoStraw[] }) =>
        (location.embryoStraws ?? []).map(straw => ({
          ...location,
          embryoStrawCode: straw.embryoStrawCode,
          embryoStrawNumber: straw.embryoStrawNumber,
          embryoGrade: straw.embryoGrade,
          embryoStrawId: straw.embryoStrawId,
          embryoCountPerStraw: straw.embryoCountPerStraw,
        })),
      );
    }

    return specimenLocations;
  })();

  const totalSelectedQuantity: number = checkOutSpecimens
    .filter(
      (specimen: CheckOutSpecimen) =>
        specimen.selected === true && typeof specimen.quantity === 'number' && !isNaN(specimen.quantity),
    )
    .reduce((sum: number, specimen: CheckOutSpecimen) => sum + specimen.quantity, 0);

  const getRowKey = (specimenLocation: SpecimenLocationWithStraw) =>
    `${specimenLocation.storageCanisterId}-${specimenLocation.embryoStrawId ?? 'none'}`;

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, specimenLocation: SpecimenLocationWithStraw) => {
    const isChecked = e.target.checked;
    const newCheckOutSpecimens = [...checkOutSpecimens];

    const index = newCheckOutSpecimens.findIndex(
      c =>
        c.selectedCanisterId === specimenLocation.storageCanisterId &&
        (c.embryoStrawId ?? 'none') === (specimenLocation.embryoStrawId ?? 'none'),
    );

    if (index !== -1) {
      newCheckOutSpecimens[index].selected = isChecked;
      if (!isChecked) newCheckOutSpecimens[index].quantity = 0;
    } else if (isChecked) {
      newCheckOutSpecimens.push({
        specimenId: pendingUseTransaction.specimenId,
        embryoStrawId: specimenLocation.embryoStrawId,
        quantity: specimenLocation.embryoCountPerStraw ?? 0,
        quantityUsed: 0,
        quantityLost: 0,
        transactionLinkId: pendingUseTransaction.inventoryTransactionId,
        selectedCanisterId: specimenLocation.storageCanisterId,
        destinationCanisterId: 0,
        selected: true,
      });
    }

    setCheckOutSpecimens(newCheckOutSpecimens);
  };

  return (
    <div className="pending-use-selected-checkout">
      <div className="desk-pending-use-selected">
        <div className="specimen-table">
          <table>
            <thead>
              <tr>
                <th>{TABLE_HEADER_CONSTANTS.SELECT}</th>
                <th>{TABLE_HEADER_CONSTANTS.SITE}</th>
                <th>{TABLE_HEADER_CONSTANTS.TANK}</th>
                <th>{TABLE_HEADER_CONSTANTS.CANISTER}</th>
                <th>{TABLE_HEADER_CONSTANTS.CANISTER_QTY}</th>
                {isFemale && (
                  <>
                    <th>{TABLE_HEADER_CONSTANTS.STRAW_LOT}</th>
                    <th>{TABLE_HEADER_CONSTANTS.STRAW_NUMBER}</th>
                  </>
                )}
                <th>{TABLE_HEADER_CONSTANTS.CHECK_OUT_QTY}</th>
              </tr>
            </thead>
            {pendingUseTransaction.specimenLocations &&
              pendingUseTransaction.specimenLocations?.length > 0 &&
              checkOutSpecimens &&
              checkOutSpecimens.length > 0 && (
                <tbody>
                  {specimenRows
                    .filter(location => location.storageCanister?.storageTank?.isTravelingTank === false)
                    .map((specimenLocation, index) => (
                      <tr className={handleRowClass(index, specimenLocation, false, false)} key={getRowKey(specimenLocation)}>
                        <td>
                          <input
                            type="checkbox"
                            checked={checkOutSpecimens.some(
                              specimenItem =>
                                specimenItem.selectedCanisterId === specimenLocation.storageCanisterId &&
                                (specimenItem.embryoStrawId ?? 'none') === (specimenLocation.embryoStrawId ?? 'none') &&
                                specimenItem.selected,
                            )}
                            onChange={event => handleCheckboxChange(event, specimenLocation)}
                          />
                        </td>
                        <td>{specimenLocation?.storageCanister?.storageTank?.storageSite?.name}</td>
                        <td>{specimenLocation?.storageCanister?.storageTank?.name}</td>
                        <td>{specimenLocation?.storageCanister?.name}</td>
                        <td>{specimenLocation?.quantity}</td>
                        {isFemale && (
                          <>
                            <td>
                              {specimenLocation?.embryoStrawCode ?? '-'}
                              {specimenLocation?.embryoStrawCode && (
                                <Tooltip>
                                  {`${LABEL_CONSTANTS.EMBRYO_GRADE}: ${specimenLocation.embryoGrade ?? 'N/A'}`} <br />
                                  {`${LABEL_CONSTANTS.EMBRYO_STAGE}: ${specimenLocation.embryoStage ?? 'N/A'}`}
                                </Tooltip>
                              )}
                            </td>
                            <td>{specimenLocation?.embryoStrawNumber ?? '-'}</td>
                          </>
                        )}

                        <td>
                          {isFemale ? (
                            <NumberInput value={specimenLocation.embryoCountPerStraw} />
                          ) : (
                            <NumberInput
                              onChange={e => handleCheckoutQuantityChange(e, specimenLocation)}
                              value={getSelectedQuantity(specimenLocation.storageCanisterId)}
                            />
                          )}
                        </td>
                      </tr>
                    ))}

                  {pendingUseTransaction.specimenLocations
                    ?.filter(location => location.storageCanister?.storageTank?.isTravelingTank === true)
                    ?.filter(location => {
                      if (isFemale) {
                        return location.embryoStraws && location.embryoStraws.length > 0;
                      }
                      return true;
                    })
                    ?.map((specimenLocation, index) => (
                      <tr
                        className={handleRowClass(index, specimenLocation, false, true) + ' disabled'}
                        key={getRowKey(specimenLocation)}>
                        <td>{specimenLocation?.storageCanister?.storageTank?.storageSite?.name}</td>
                        <td>{specimenLocation?.storageCanister?.storageTank?.name}</td>
                        <td>{specimenLocation?.storageCanister?.name}</td>
                        <td>{specimenLocation?.quantity}</td>
                        <td>Traveling Tank</td>
                      </tr>
                    ))}
                  <tr>
                    <td colSpan={isFemale ? 7 : 5}></td>
                    <td colSpan={1}>
                      <div className="quantity">{totalSelectedQuantity}</div>
                    </td>
                  </tr>
                </tbody>
              )}
          </table>
        </div>
        {isTravelingTank &&
          (!isFemale ||
            pendingUseTransaction.specimenLocations?.some(
              loc =>
                loc.storageCanister?.storageTank?.isTravelingTank === true && loc.embryoStraws && loc.embryoStraws.length > 0,
            )) && (
            <div>
              <br />
              <InfoWarning
                message={`Items stored in Traveling tanks can't be checked out. Please process a Move transaction if necessary`}
              />
            </div>
          )}
      </div>

      {/* Mobile View */}
      <div className="xs-pending-use-selected">
        {specimenRows && specimenRows.length > 0 && checkOutSpecimens && checkOutSpecimens.length > 0 && (
          <>
            {specimenRows
              .filter(location => location.storageCanister?.storageTank?.isTravelingTank === false)
              .map((specimenLocation, index) => {
                const isChecked = checkOutSpecimens.some(
                  c =>
                    c.selectedCanisterId === specimenLocation.storageCanisterId &&
                    (c.embryoStrawId ?? 'none') === (specimenLocation.embryoStrawId ?? 'none') &&
                    c.selected,
                );
                return (
                  <div className={handleRowClass(index, specimenLocation, true, false)} key={getRowKey(specimenLocation)}>
                    <div className="body">
                      <div className="select-checkbox">
                        <input type="checkbox" checked={isChecked} onChange={e => handleCheckboxChange(e, specimenLocation)} />
                        <label className="specimen-location-label">
                          {`${specimenLocation?.storageCanister?.storageTank?.storageSite?.name}/${specimenLocation?.storageCanister?.storageTank?.name}/${specimenLocation?.storageCanister?.name}`}
                          {isFemale && specimenLocation.embryoStrawCode ? (
                            <>
                              {` - ${specimenLocation.embryoStrawCode}`}
                              <Tooltip>
                                {`${LABEL_CONSTANTS.EMBRYO_GRADE}: ${specimenLocation.embryoGrade ?? 'N/A'}`} <br />
                                {`${LABEL_CONSTANTS.EMBRYO_STAGE}: ${specimenLocation.embryoStage ?? 'N/A'}`}
                              </Tooltip>
                            </>
                          ) : null}
                        </label>
                      </div>

                      <div className="specimen-location">
                        <label>{`Canister Qty: ${specimenLocation?.quantity}`}</label>
                      </div>

                      {/* Quantity Input */}
                      <div className="selected-specimen-qty">
                        <label>{'Selected Qty'}</label>
                        {isFemale ? (
                          <NumberInput value={specimenLocation.embryoCountPerStraw ?? 0} />
                        ) : (
                          <NumberInput
                            onChange={e => handleCheckoutQuantityChange(e, specimenLocation)}
                            value={getSelectedQuantity(specimenLocation.storageCanisterId)}
                            disabled={!isChecked}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

            {specimenRows
              .filter(location => location.storageCanister?.storageTank?.isTravelingTank === true)
              .map((specimenLocation, index) => (
                <div
                  className={handleRowClass(index, specimenLocation, true, true) + ' disabled'}
                  key={getRowKey(specimenLocation)}>
                  <div className="body">
                    <div className="specimen-location">
                      <label className="specimen-location-label">
                        {`${specimenLocation?.storageCanister?.storageTank?.storageSite?.name}/${specimenLocation?.storageCanister?.storageTank?.name}/${specimenLocation?.storageCanister?.name}`}
                      </label>
                      <label>{`Canister Qty: ${specimenLocation?.quantity}`}</label>
                    </div>
                    <div className="selected-specimen-qty">
                      <label>{'Traveling Tank'}</label>
                    </div>
                  </div>
                </div>
              ))}

            <div className={handleSummaryClass()}>
              <div className="total-quantity">
                <label>Total Selected: </label>
                {totalSelectedQuantity}
              </div>
            </div>
          </>
        )}

        {isTravelingTank &&
          (!isFemale ||
            pendingUseTransaction.specimenLocations?.some(
              loc =>
                loc.storageCanister?.storageTank?.isTravelingTank === true && loc.embryoStraws && loc.embryoStraws.length > 0,
            )) && (
            <div>
              <br />
              <InfoWarning
                message={`Items stored in Traveling tanks can't be checked out. Please process a Move transaction if necessary`}
              />
            </div>
          )}
      </div>
    </div>
  );
};

export default PendingUseSelectQuantityTable;
